import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ExternalLink, TrendingUp, Star, Search, Filter, MapPin, Phone, Mail, Globe, Linkedin, Facebook, Twitter, Instagram } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Businesses = () => {
  const { user, token } = React.useContext(AuthContext);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const navigate = useNavigate();

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    filterAndSortBusinesses();
  }, [businesses, searchQuery, selectedIndustry, sortBy]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    }
  };

  const filterAndSortBusinesses = () => {
    let filtered = [...businesses];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(b =>
        b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.industry?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by industry
    if (selectedIndustry !== 'all') {
      filtered = filtered.filter(b => b.industry === selectedIndustry);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'industry') return (a.industry || '').localeCompare(b.industry || '');
      if (sortBy === 'featured') return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      return 0;
    });

    setFilteredBusinesses(filtered);
  };

  const industries = [...new Set(businesses.map(b => b.industry).filter(Boolean))];

  const getSocialIcon = (platform) => {
    const icons = {
      linkedin: <Linkedin className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      twitter: <Twitter className="w-4 h-4" />,
      instagram: <Instagram className="w-4 h-4" />
    };
    return icons[platform.toLowerCase()] || <Globe className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="businesses-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Business Showcase
          </h1>
          <p className="text-gray-600 text-lg">
            Discover {businesses.length} women-owned businesses in our community
          </p>
        </div>

        {/* Filters & Search */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/80 backdrop-blur p-6 rounded-xl shadow-lg">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Industry Filter */}
          <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
            <SelectTrigger>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Industries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map((industry) => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort By */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <TrendingUp className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A-Z)</SelectItem>
              <SelectItem value="industry">Industry</SelectItem>
              <SelectItem value="featured">Featured First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-gray-600">
          Showing {filteredBusinesses.length} of {businesses.length} businesses
        </div>

        {/* Business Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBusinesses.map((business) => (
            <Card key={business.id} className="glass hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden group" data-testid={`business-card-${business.id}`}>
              {/* Cover Image */}
              {business.cover_image_url && (
                <div className="w-full h-40 overflow-hidden">
                  <img 
                    src={business.cover_image_url} 
                    alt={`${business.name} cover`} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
              )}
              
              {/* Featured Badges */}
              {business.is_top_placed && (
                <Badge className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white z-10 shadow-lg">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Top Placement
                </Badge>
              )}
              {business.is_featured && !business.is_top_placed && (
                <Badge className="absolute top-4 right-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white z-10 shadow-lg">
                  <Star className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              )}
              
              <CardHeader className="pb-3">
                {/* Logo */}
                {business.logo_url && (
                  <div className="w-20 h-20 mb-3 rounded-full overflow-hidden bg-white shadow-md border-4 border-white -mt-10 relative z-10">
                    <img src={business.logo_url} alt={business.name} className="w-full h-full object-cover" />
                  </div>
                )}
                
                <CardTitle className="text-xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                  {business.name}
                </CardTitle>
                
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {business.industry && (
                    <Badge variant="outline" className="text-xs">
                      {business.industry}
                    </Badge>
                  )}
                  {business.tagline && (
                    <p className="text-sm text-gray-600 italic mt-1">"{business.tagline}"</p>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Description */}
                <p className="text-sm text-gray-700 line-clamp-3">
                  {business.description}
                </p>

                {/* Quick Facts */}
                {business.quick_facts && business.quick_facts.length > 0 && (
                  <div className="bg-rose-50 rounded-lg p-3 space-y-1">
                    {business.quick_facts.slice(0, 3).map((fact, idx) => (
                      <p key={idx} className="text-xs text-gray-700 flex items-start gap-2">
                        <span className="text-rose-500 mt-0.5">•</span>
                        {fact}
                      </p>
                    ))}
                  </div>
                )}

                {/* Contact Info */}
                <div className="space-y-2 text-sm">
                  {business.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-rose-500" />
                      <a href={`mailto:${business.email}`} className="hover:text-rose-600 truncate">
                        {business.email}
                      </a>
                    </div>
                  )}
                  {business.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-rose-500" />
                      <a href={`tel:${business.phone}`} className="hover:text-rose-600">
                        {business.phone}
                      </a>
                    </div>
                  )}
                  {business.location && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-rose-500" />
                      <span className="truncate">{business.location}</span>
                    </div>
                  )}
                </div>

                {/* Social Media Links */}
                {(business.linkedin_url || business.facebook_url || business.twitter_url || business.instagram_url) && (
                  <div className="flex items-center gap-2 pt-2 border-t">
                    {business.linkedin_url && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="p-2 h-auto hover:bg-blue-50 hover:text-blue-600"
                        onClick={() => window.open(business.linkedin_url, '_blank')}
                      >
                        <Linkedin className="w-4 h-4" />
                      </Button>
                    )}
                    {business.facebook_url && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="p-2 h-auto hover:bg-blue-50 hover:text-blue-600"
                        onClick={() => window.open(business.facebook_url, '_blank')}
                      >
                        <Facebook className="w-4 h-4" />
                      </Button>
                    )}
                    {business.twitter_url && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="p-2 h-auto hover:bg-sky-50 hover:text-sky-600"
                        onClick={() => window.open(business.twitter_url, '_blank')}
                      >
                        <Twitter className="w-4 h-4" />
                      </Button>
                    )}
                    {business.instagram_url && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="p-2 h-auto hover:bg-pink-50 hover:text-pink-600"
                        onClick={() => window.open(business.instagram_url, '_blank')}
                      >
                        <Instagram className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}

                {/* Website Button */}
                {business.website && (
                  <Button 
                    className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white flex items-center justify-center gap-2"
                    onClick={() => window.open(business.website, '_blank')}
                    data-testid={`visit-website-${business.id}`}
                  >
                    <Globe className="w-4 h-4" />
                    Visit Website
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredBusinesses.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No businesses found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Businesses;